import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  MeetingData,
  NoteItem
} from 'src/content/MiniExperts/SmartMeet/types/types';
import type { StringKeys } from 'src/types/base';
import audioRecorderService from 'src/utils/audioRecorder';
import logger from 'src/utils/logger';

export const stopRecordingAsync = createAsyncThunk(
  'smartMeet/stopRecordingAsync',
  async (_, { rejectWithValue }) => {
    try {
      logger.log('Stopping audio recording...');
      const blob = await audioRecorderService.stop();
      return blob;
    } catch (err: any) {
      logger.error('Stopping audio recording error:', err);
      return rejectWithValue(err.message);
    }
  }
);

export const startRecordingAsync = createAsyncThunk(
  'smartMeet/startRecordingAsync',
  async (meetingName: string, { rejectWithValue }) => {
    try {
      await audioRecorderService.start(meetingName);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const resumeRecordingAsync = createAsyncThunk(
  'smartMeet/resumeRecordingAsync',
  async (meetingName: string, { rejectWithValue }) => {
    try {
      await audioRecorderService.resumeExistingRecording(meetingName);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export enum RecordingProgress {
  StoppingAudio = 'L_SM_SR',
  UploadingAudio = 'L_SM_UA',
  GeneratingTranscript = 'L_SM_GT',
  ProcessingAudio = 'L_SM_PA',
  GeneratingData = 'L_SM_MD',
  MeetingCreated = 'L_SM_MC',
  MeetingInDraft = 'L_SM_DMC'
}

interface IProcessingMeeting {
  status_code: RecordingProgress;
  state?: boolean;
  status_details?: string;
}

export type { IProcessingMeeting };

export interface SmartMeetState {
  progress: IProcessingMeeting | null;
  currentPage: number;
  seconds: number;
  isRecording: boolean;
  meetingData?: MeetingData;
  draftMeeting?: MeetingData;
  isPaused: boolean;
  isMinimized: boolean;
  errorMsg: string | null;
  audioURL: string | null;
  notes?: NoteItem[];
  uploadedFileResponse?: StringKeys;
  refreshKey: number;
  smChatModel: string | null;
}

const initialState: SmartMeetState = {
  seconds: 0,
  currentPage: 0,
  progress: null,
  isRecording: false,
  meetingData: null,
  draftMeeting: null,
  isPaused: false,
  isMinimized: false,
  errorMsg: null,
  audioURL: null,
  notes: [],
  uploadedFileResponse: null,
  refreshKey: 0,
  smChatModel: null
};

export const smartMeetSlice = createSlice({
  name: 'smartMeet',
  initialState,
  reducers: {
    setSmChatModel(state, action: PayloadAction<string>) {
      state.smChatModel = action.payload;
    },
    pauseRecording(state) {
      state.isPaused = true;
      audioRecorderService.pause();
    },
    resumeRecording(state) {
      state.isPaused = false;
      audioRecorderService.resume();
    },
    minimizeRecording(state) {
      state.isMinimized = true;
    },
    maximizeRecording(state) {
      state.isMinimized = false;
    },
    setProgress(state, action: PayloadAction<IProcessingMeeting | null>) {
      if (!action.payload) {
        state.progress = null;
        state.uploadedFileResponse = null;
        return;
      }
      state.progress = {
        state: true,
        status_details: undefined,
        ...action.payload
      };
    },
    setError(state, action: PayloadAction<string | null>) {
      state.errorMsg = action.payload;
    },
    addNote(state, action: PayloadAction<NoteItem>) {
      state.notes.push(action.payload);
    },
    setNotes(state, action: PayloadAction<NoteItem[]>) {
      state.notes = action.payload;
    },
    removeNote(state, action: PayloadAction<NoteItem>) {
      state.notes = state.notes.filter((note) => note !== action.payload);
    },
    setMeetingData(state, action: PayloadAction<MeetingData>) {
      state.meetingData = action.payload;
    },
    setDraftMeeting(state, action: PayloadAction<MeetingData>) {
      state.draftMeeting = action.payload;
    },
    tick(state) {
      if (state.isRecording && !state.isPaused) {
        state.seconds += 1;
      }
    },
    resetTimer(state) {
      state.seconds = 0;
    },
    setSeconds(state, action: PayloadAction<number>) {
      state.seconds = action.payload;
    },
    setUploadedFileResponse(state, action: PayloadAction<StringKeys>) {
      state.uploadedFileResponse = action.payload;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    incrementRefreshKey(state) {
      state.refreshKey += 1;
    },
    discardRecording(state) {
      state.isRecording = false;
      state.isPaused = false;
      state.isMinimized = false;
      state.progress = null;
      state.draftMeeting = null;
      state.notes = [];
      state.seconds = 0;
      state.audioURL = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(stopRecordingAsync.fulfilled, (state, action) => {
        state.isRecording = false;
        state.isPaused = false;
        state.progress = {
          status_code: RecordingProgress.UploadingAudio,
          state: true
        };
        state.isMinimized = false;
        state.audioURL = (action.payload as string) || null;
      })
      .addCase(stopRecordingAsync.rejected, (state, action) => {
        state.errorMsg =
          (action.payload as string) || 'Error stopping recording.';
        state.isRecording = false;
        state.isPaused = false;
        state.isMinimized = false;
        state.audioURL = null;
      })
      .addCase(startRecordingAsync.fulfilled, (state) => {
        state.isRecording = true;
        state.isPaused = false;
        state.errorMsg = null;
        state.progress = null;
        state.seconds = 1;
      })
      .addCase(startRecordingAsync.rejected, (state, action) => {
        state.errorMsg =
          (action.payload as string) || 'Error starting recording.';
        state.isRecording = false;
        state.isPaused = false;
      })
      .addCase(resumeRecordingAsync.fulfilled, (state) => {
        state.isRecording = true;
        state.isPaused = false;
        state.errorMsg = null;
      })
      .addCase(resumeRecordingAsync.rejected, (state, action) => {
        state.errorMsg =
          (action.payload as string) || 'Error resuming recording.';
        state.isRecording = false;
        state.isPaused = false;
      })
      .addCase(stopRecordingAsync.pending, (state) => {
        state.errorMsg = null;
      });
  }
});

export const {
  setSmChatModel,
  pauseRecording,
  resumeRecording,
  minimizeRecording,
  maximizeRecording,
  setError,
  addNote,
  setNotes,
  removeNote,
  setMeetingData,
  setDraftMeeting,
  tick,
  setProgress,
  resetTimer,
  setUploadedFileResponse,
  setSeconds,
  setCurrentPage,
  incrementRefreshKey,
  discardRecording
} = smartMeetSlice.actions;

export const reducer = smartMeetSlice.reducer;
